/**
 * SendEmailModal — Prompts the admin for one or more email addresses, then
 * sends the portco daily report to those recipients via /reports/:id/send-email.
 *
 * Used from the portco detail page's "Send Email" button.
 */

import { useEffect, useState } from "react";
import { sendPortcoEmail } from "../utils/PowerBIApiCall";

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

interface SendEmailModalProps {
  portcoId: string;
  /** Optional "as of" date (YYYY-MM-DD) — passed through to the send endpoint. */
  reportDate?: string;
  onClose: () => void;
  onSent: (message: string) => void;
}

export function SendEmailModal({
  portcoId,
  reportDate,
  onClose,
  onSent,
}: SendEmailModalProps) {
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !sending) onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, sending]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed) {
      setError("Please enter an email address");
      return;
    }
    // Allow comma-separated lists for the occasional multi-recipient send.
    const recipients = trimmed
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const invalid = recipients.find((r) => !isValidEmail(r));
    if (invalid) {
      setError(`"${invalid}" is not a valid email address`);
      return;
    }
    setSending(true);
    setError(null);
    try {
      const result = await sendPortcoEmail(portcoId, reportDate, recipients);
      onSent(result.message);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to send email");
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-navy-950/40 backdrop-blur-[2px] flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget && !sending) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-pop border border-gray-200/70 w-full max-w-sm mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Send Email</h3>
        <p className="text-sm text-gray-500 mb-4">
          Enter the recipient&rsquo;s email address. Separate multiple addresses
          with commas.
          {reportDate && (
            <>
              <br />
              <span className="text-amber-700">
                Report will be sent as of {reportDate}.
              </span>
            </>
          )}
        </p>
        {error && (
          <div className="mb-3 bg-red-50 text-red-700 text-sm px-3 py-2 rounded border border-red-200">
            {error}
          </div>
        )}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="you@company.com"
          autoFocus
          disabled={sending}
          className="w-full border border-gray-200/70 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-wmp-500/40 focus:border-wmp-500 mb-4 disabled:bg-gray-50"
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
        />
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200/70 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="px-4 py-2 text-sm text-white bg-wmp-500 rounded-lg hover:bg-wmp-600 disabled:opacity-50"
          >
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
